import React, { useState } from 'react';
import { Download } from 'lucide-react';

/**
 * Button that exports data to CSV
 *
 * @param {Object[]} data - The data to export
 * @param {string[]} columnHeaders - The headers for the CSV columns
 * @param {Object[]} columns - Column configuration objects with 'data' property mapping to data fields
 * @param {string} filename - Base filename for the export (without timestamp or extension)
 */
function ExportButton({ columnHeaders, columns, data, disabled = false, filename }) {
    const [isExporting, setIsExporting] = useState(false);

    const convertToCSV = (data) => {
        const headers = `${columnHeaders.join(',')}\n`;
        const rows = data
            .map((row) =>
                columnHeaders
                    .map((header) => {
                        const key = columns.find(
                            (col) => col.data && columnHeaders.indexOf(header) === columns.indexOf(col),
                        )?.data;
                        return key ? `"${row[key]}"` : '';
                    })
                    .join(','),
            )
            .join('\n');
        return headers + rows;
    };

    const handleExportClick = async () => {
        if (isExporting) return;

        setIsExporting(true);

        try {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const blob = new Blob([convertToCSV(data)], { type: 'text/csv;charset=utf-8;' });

            const objectUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            document.body.appendChild(link);
            link.href = objectUrl;
            link.download = `${filename}-${timestamp}.csv`;
            link.click();

            document.body.removeChild(link);
            setTimeout(() => {
                if (window.URL && typeof window.URL.revokeObjectURL === 'function') {
                    window.URL.revokeObjectURL(objectUrl);
                }
            }, 200);
        } catch (error) {
            console.error('Export error:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <button
            aria-label="export entries"
            className="p-1 rounded-full export-button-hover"
            onClick={handleExportClick}
            disabled={isExporting || disabled}
            title={disabled ? 'No entries to export' : 'Export Entries'}
        >
            {isExporting ? (
                <span className="w-5 h-5 spinner animate-spin inline-block text-black" />
            ) : (
                <Download className="h-5 w-5 text-black" />
            )}
        </button>
    );
}

export { ExportButton };
